import React from 'react';
import { siteConfig, type SiteConfig, type LanguageConfig, type SiteFeature, type AuthFeatureConfig } from './site-config';
import { languageData, type LanguageData } from './language-data';

// Утилиты для работы с языками
export function getLanguageConfig(langCode: string): LanguageConfig | null {
    return siteConfig.languages.find(lang => lang.code === langCode && lang.enabled) || null;
}

export function getDefaultLanguage(): LanguageConfig {
    const enabledLanguages = siteConfig.languages.filter(lang => lang.enabled);
    if (enabledLanguages.length === 0) {
        throw new Error('Нет доступных языков в конфигурации сайта');
    }
    
    const byCode = enabledLanguages.find(lang => lang.code === siteConfig.defaultLanguage);
    if (byCode) {
        return byCode;
    }
    
    const byFlag = enabledLanguages.find(lang => lang.isDefault);
    return byFlag || enabledLanguages[0];
}

export function getEnabledLanguages(): LanguageConfig[] {
    return siteConfig.languages.filter(lang => lang.enabled);
}

export function getLanguageFromPath(pathname: string): LanguageConfig {
    const enabledLanguages = getEnabledLanguages();
    
    // Проверяем каждый язык
    for (const lang of enabledLanguages) {
        if (lang.path === '') {
            // Язык по умолчанию без префикса
            const hasOtherLangPrefix = enabledLanguages.some(other => 
                other.path && (pathname === other.path || pathname.startsWith(other.path + '/'))
            );
            if (!hasOtherLangPrefix) {
                return lang;
            }
        } else {
            // Язык с префиксом
            if (pathname === lang.path || pathname.startsWith(lang.path + '/')) {
                return lang;
            }
        }
    }
    
    return getDefaultLanguage();
}

export function getLanguageData(langCode: string): LanguageData | null {
    return languageData[langCode as keyof typeof languageData] || null;
}

export function buildLanguagePath(langCode: string, path: string): string {
    const langConfig = getLanguageConfig(langCode);
    if (!langConfig) return path;
    
    // Убираем префикс текущего языка из пути
    const enabledLanguages = getEnabledLanguages();
    let cleanPath = path;
    
    for (const lang of enabledLanguages) {
        if (lang.path && path.startsWith(lang.path)) {
            cleanPath = path.substring(lang.path.length) || '/';
            break;
        }
    }
    
    // Добавляем префикс нового языка
    if (langConfig.path === '') {
        return cleanPath;
    } else {
        return langConfig.path + (cleanPath === '/' ? '' : cleanPath);
    }
}

/**
 * Удаляет языковой префикс из пути, если он присутствует
 */
function removeLanguagePrefix(path: string): string {
    if (!path || path === '/') return '/';
    
    const enabledLanguages = getEnabledLanguages();
    
    // Проверяем каждый язык с префиксом
    for (const lang of enabledLanguages) {
        if (lang.path && path.startsWith(lang.path + '/')) {
            return path.substring(lang.path.length);
        } else if (lang.path && path === lang.path) {
            return '/';
        }
    }
    
    return path;
}

/**
 * Добавляет языковой префикс к пути в зависимости от текущего языка
 * @param path - Путь для локализации (например, '/shop/item/123')
 * @param langCode - Код языка (например, 'ru', 'en', 'de')
 * @returns Локализованный путь (например, '/ru/shop/item/123' для языка 'ru')
 */
export function localizePath(path: string, langCode: string): string {
    if (!path) return '/';
    
    // Если путь уже является внешней ссылкой (http/https), возвращаем как есть
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
        return path;
    }
    
    // Если путь начинается с #, возвращаем как есть (якорные ссылки)
    if (path.startsWith('#')) {
        return path;
    }
    
    const langConfig = getLanguageConfig(langCode);
    if (!langConfig) {
        // Если язык не найден, убираем существующие префиксы и возвращаем чистый путь
        return removeLanguagePrefix(path);
    }
    
    // Убираем существующие языковые префиксы
    const cleanPath = removeLanguagePrefix(path);
    
    // Если путь является корневым
    if (cleanPath === '/') {
        return langConfig.path || '/';
    }
    
    // Добавляем префикс текущего языка
    if (langConfig.path === '') {
        // Для языка по умолчанию (без префикса) возвращаем чистый путь
        return cleanPath;
    } else {
        // Для языков с префиксом добавляем префикс
        return langConfig.path + cleanPath;
    }
}

// Утилиты для работы с функциями
export function isFeatureEnabled(featureName: keyof typeof siteConfig.features): boolean {
    return siteConfig.features[featureName]?.enabled ?? false;
}

export function getFeatureConfig<T = any>(featureName: keyof typeof siteConfig.features): T | null {
    const feature = siteConfig.features[featureName];
    return feature?.enabled ? (feature.config as T) : null;
}

export function getAuthFeatureConfig(): AuthFeatureConfig | null {
    return getFeatureConfig<AuthFeatureConfig>('auth');
}

export function getEnabledFeatures(): Array<keyof typeof siteConfig.features> {
    return Object.keys(siteConfig.features).filter(key => 
        siteConfig.features[key as keyof typeof siteConfig.features].enabled
    ) as Array<keyof typeof siteConfig.features>;
}

// Утилиты для работы с мета-данными
export function getLanguageMetadata(langCode: string) {
    const langConfig = getLanguageConfig(langCode);
    return langConfig?.metadata || null;
}

export function getLanguageContactInfo(langCode: string) {
    const langConfig = getLanguageConfig(langCode);
    return langConfig?.contactInfo || null;
}

export function getLanguageCurrency(langCode: string) {
    const langConfig = getLanguageConfig(langCode);
    return langConfig?.currency || null;
}

// Утилиты для работы с переводами
export function getTranslation(langCode: string, key: string, section?: string): string {
    const langData = getLanguageData(langCode);
    if (!langData) return key;
    
    if (section) {
        const sectionData = langData.content[section as keyof typeof langData.content];
        if (sectionData && typeof sectionData === 'object') {
            const value = sectionData[key];
            return typeof value === 'string' ? value : key;
        }
        return key;
    }
    
    // Ищем в разных секциях
    return langData.content.common[key] || 
           langData.forms.labels[key] || 
           langData.forms.buttons[key] || 
           langData.forms.placeholders[key] || 
           key;
}

export function getFormTranslation(langCode: string, type: 'labels' | 'buttons' | 'placeholders' | 'validation', key: string): string {
    const langData = getLanguageData(langCode);
    if (!langData) return key;
    
    return langData.forms[type][key] || key;
}

export function getNavigationData(langCode: string, section: 'main' | 'company' | 'bottomBar' | 'footer') {
    const langData = getLanguageData(langCode);
    if (!langData) return [];
    
    return langData.navigation[section];
}

// Утилита для создания компонента условного рендеринга
export function createFeatureComponent<T extends keyof typeof siteConfig.features>(
    featureName: T, 
    component: React.ComponentType<any>, 
    fallback?: React.ComponentType<any>
) {
    return function ConditionalFeatureComponent(props: any) {
        if (isFeatureEnabled(featureName)) {
            return React.createElement(component, props);
        }
        return fallback ? React.createElement(fallback, props) : null;
    };
}

// Утилита для получения настроек API
export function getApiConfig() {
    return siteConfig.api;
}

// Утилита для получения настроек кэша
export function getCacheConfig() {
    return siteConfig.cache;
}

// Утилита для получения общих настроек
export function getGeneralConfig() {
    return siteConfig.general;
}

// Утилита для получения SEO настроек
export function getSeoConfig() {
    return siteConfig.seo;
}

// Хук для использования конфигурации в React компонентах
export function useConfig() {
    return {
        siteConfig,
        getLanguageConfig,
        getDefaultLanguage,
        getEnabledLanguages,
        getLanguageFromPath,
        getLanguageData,
        buildLanguagePath,
        isFeatureEnabled,
        getFeatureConfig,
        getEnabledFeatures,
        getLanguageMetadata,
        getLanguageContactInfo,
        getLanguageCurrency,
        getTranslation,
        getFormTranslation,
        getNavigationData,
        getApiConfig,
        getCacheConfig,
        getGeneralConfig,
        getSeoConfig
    };
}

// Экспорт основного конфига для удобства
export { siteConfig } from './site-config';
export { languageData } from './language-data'; 
